/**
 * OpenAIP aviation layers (sign-off 05-004 and 04-010; docs/specs/map.md §19): airspace, airports, navaids and
 * reporting points from OpenAIP's area lists, through the provider proxy with the organisation's own key (ADR-016,
 * decision 6), and the restricted, danger and prohibited areas that NAIPS NOTAMs activate.
 *
 * Diurnal sources: `aircraft-map.js` `AIRSPACE_STYLES` 9966, `fetchAndRenderAirspaces` 10086 (zoom 6, labels from
 * zoom 7, R/D/P halved until active), `_showAirspaceDetail` 10448, `_parseActiveRpdFromNotams` 11868,
 * `_notamExtractTimeSlots` 10380, `_applyAirspaceActivationStyles` 11977, `_extractAirspaceDesignator` 12008.
 *
 * Changed from the Diurnal tool:
 * - The words of OpenAIP's codes are OpenAIP's own (its response schemas, `tests/fixtures/openaip/schemas/`): the
 *   tool called type 4 "Alert/Warning" (it is a CTR), type 8 "CTR" (a TRA), 9 "TMA" (a TSA) and 14 "MOA" (a MATZ),
 *   and its class table was one off (class 0 is A).
 * - The tool asked OpenAIP for the view on every pan; OpenAIP says its area lists are "mainly intended for export
 *   use-cases" and rate-limits them (HTTP 429 after a few quick requests, 20 Oct 2026). Teslyn asks fixed 2-degree
 *   cells around the project once, keeps them on the laptop for a week and reuses them offline (`openAipCells`).
 * - A NOTAM's designators are read whole: "R155AB" activates R155A and R155B (the tool's pattern read nothing in it),
 *   and the activation periods printed under the NOTAM ("2610200000 TO 2610200900") decide whether it is active now.
 */
import type { IdentifyRow } from './map-kml.js';
import type {
  FeatureStyle,
  LayerResult,
  LegendEntry,
  MapColour,
  MapFeature,
  MapStroke,
} from './map-layers.js';
import { notamDesignators, notamTimeState, utcText } from './notam-map.js';
import type { NaipsNotam } from './naips-briefing.js';
import type { GeoBounds } from './map-geo.js';
import type { LatLng } from './planning.js';
import { OPENAIP_MAX_PAGES, OPENAIP_PAGE_LIMIT, type OpenAipAreaOp } from './provider-protocols.js';

// ------------------------------------------------------------------------------------------------ OpenAIP's codes

/**
 * OpenAIP's words that Teslyn writes the Australian way (decision 661: "Manoeuvring", "Defence", "Centre"), and
 * OpenAIP's typo "Arial"; every other word of the tables below is OpenAIP's own.
 */
export const OPENAIP_RESPELLED: Readonly<Record<string, string>> = {
  'Terminal Maneuvering Area (TMA)': 'Terminal Manoeuvring Area (TMA)',
  'Air Defense Identification Zone (ADIZ)': 'Air Defence Identification Zone (ADIZ)',
  'Aeroclub And Arial Work Area': 'Aero Club and Aerial Work Area',
  Center: 'Centre',
};

/** OpenAIP airspace `type` (response schema `airspace-schema.json`). */
export const OPENAIP_AIRSPACE_TYPES: Readonly<Record<number, string>> = {
  0: 'Other',
  1: 'Restricted',
  2: 'Danger',
  3: 'Prohibited',
  4: 'Controlled Tower Region (CTR)',
  5: 'Transponder Mandatory Zone (TMZ)',
  6: 'Radio Mandatory Zone (RMZ)',
  7: 'Terminal Manoeuvring Area (TMA)',
  8: 'Temporary Reserved Area (TRA)',
  9: 'Temporary Segregated Area (TSA)',
  10: 'Flight Information Region (FIR)',
  11: 'Upper Flight Information Region (UIR)',
  12: 'Air Defence Identification Zone (ADIZ)',
  13: 'Airport Traffic Zone (ATZ)',
  14: 'Military Airport Traffic Zone (MATZ)',
  15: 'Airway',
  16: 'Military Training Route (MTR)',
  17: 'Alert Area',
  18: 'Warning Area',
  19: 'Protected Area',
  20: 'Helicopter Traffic Zone (HTZ)',
  21: 'Gliding Sector',
  22: 'Transponder Setting (TRP)',
  23: 'Traffic Information Zone (TIZ)',
  24: 'Traffic Information Area (TIA)',
  25: 'Military Training Area (MTA)',
  26: 'Control Area (CTA)',
  27: 'ACC Sector (ACC)',
  28: 'Aerial Sporting Or Recreational Activity',
  29: 'Low Altitude Overflight Restriction',
  30: 'Military Route (MRT)',
  31: 'TSA/TRA Feeding Route (TFR)',
  32: 'VFR Sector',
  33: 'FIS Sector',
  34: 'Lower Traffic Area (LTA)',
  35: 'Upper Traffic Area (UTA)',
  36: 'Military Controlled Tower Region (MCTR)',
};

/** OpenAIP `icaoClass`. */
export const OPENAIP_ICAO_CLASSES: Readonly<Record<number, string>> = {
  0: 'A',
  1: 'B',
  2: 'C',
  3: 'D',
  4: 'E',
  5: 'F',
  6: 'G',
  8: 'Unclassified / Special Use Airspace (SUA)',
};

/** OpenAIP airspace `activity`. */
export const OPENAIP_ACTIVITIES: Readonly<Record<number, string>> = {
  0: 'None - No specific activity (default)',
  1: 'Parachuting Activity',
  2: 'Aerobatics Activity',
  3: 'Aero Club and Aerial Work Area',
  4: 'Ultra Light Machine (ULM) Activity',
  5: 'Hang Gliding/Paragliding',
};

/** OpenAIP airport `type`. */
export const OPENAIP_AIRPORT_TYPES: Readonly<Record<number, string>> = {
  0: 'Airport (civil/military)',
  1: 'Glider Site',
  2: 'Airfield Civil',
  3: 'International Airport',
  4: 'Heliport Military',
  5: 'Military Aerodrome',
  6: 'Ultra Light Flying Site',
  7: 'Heliport Civil',
  8: 'Aerodrome Closed',
  9: 'Airport resp. Airfield IFR',
  10: 'Airfield Water',
  11: 'Landing Strip',
  12: 'Agricultural Landing Strip',
  13: 'Altiport',
};

/** OpenAIP navaid `type`. */
export const OPENAIP_NAVAID_TYPES: Readonly<Record<number, string>> = {
  0: 'DME',
  1: 'TACAN',
  2: 'NDB',
  3: 'VOR',
  4: 'VOR-DME',
  5: 'VORTAC',
  6: 'DVOR',
  7: 'DVOR-DME',
  8: 'DVORTAC',
};

/** OpenAIP frequency `type` (airports). */
export const OPENAIP_FREQUENCY_TYPES: Readonly<Record<number, string>> = {
  0: 'Approach',
  1: 'APRON',
  2: 'Arrival',
  3: 'Centre',
  4: 'CTAF',
  5: 'Delivery',
  6: 'Departure',
  7: 'FIS',
  8: 'Gliding',
  9: 'Ground',
  10: 'Information',
  11: 'Multicom',
  12: 'Unicom',
  13: 'Radar',
  14: 'Tower',
  15: 'ATIS',
  16: 'Radio',
  17: 'Other',
  18: 'AIRMET',
  19: 'AWOS',
  20: 'Lights',
  21: 'VOLMET',
  22: 'AFIS',
  23: 'ASOS',
  24: 'AWIS',
  25: 'Emergency',
  26: 'Clearance Delivery',
  27: 'Remote Com Outlet',
  28: 'Ground Com Outlet',
  29: 'Flight Service Station',
  30: 'Class C',
  31: 'Class B',
  32: 'VFR Advisory',
  33: 'TRSA',
};

/** OpenAIP runway surface `mainComposite`. */
export const OPENAIP_SURFACES: Readonly<Record<number, string>> = {
  0: 'Asphalt',
  1: 'Concrete',
  2: 'Grass',
  3: 'Sand',
  4: 'Water',
  5: 'Bituminous tar or asphalt ("earth cement")',
  6: 'Brick',
  7: 'Macadam or tarmac surface consisting of water-bound crushed rock',
  8: 'Stone',
  9: 'Coral',
  10: 'Clay',
  11: 'Laterite - a high iron clay formed in tropical areas',
  12: 'Gravel',
  13: 'Earth',
  14: 'Ice',
  15: 'Snow',
  16: 'Protective laminate usually made of rubber',
  17: 'Metal',
  18: 'Landing mat portable system usually made of aluminium',
  19: 'Pierced steel planking',
  20: 'Wood',
  21: 'Non Bituminous mix',
  22: 'Unknown',
};

// ------------------------------------------------------------------------------------------------ answers

/** A vertical limit: `unit` 0 metres, 1 feet, 6 flight level; `referenceDatum` 0 GND, 1 MSL, 2 STD. */
export interface OpenAipLimit {
  readonly value: number;
  readonly unit: number;
  readonly referenceDatum: number;
}

export interface OpenAipAirspace {
  readonly id: string;
  readonly name: string;
  readonly type: number;
  readonly icaoClass: number | null;
  readonly activity: number | null;
  readonly byNotam: boolean;
  readonly onDemand: boolean;
  readonly onRequest: boolean;
  readonly country: string | null;
  readonly upper: OpenAipLimit | null;
  readonly lower: OpenAipLimit | null;
  /** Outer ring first, then holes; [lng, lat] as OpenAIP sends them, turned into LatLng here. */
  readonly rings: ReadonlyArray<readonly LatLng[]>;
}

export interface OpenAipFrequency {
  readonly value: string;
  readonly type: number | null;
  readonly name: string | null;
  readonly primary: boolean;
}

export interface OpenAipRunway {
  readonly designator: string;
  readonly lengthM: number | null;
  readonly widthM: number | null;
  readonly surface: number | null;
  readonly operations: number | null;
}

export interface OpenAipAirport {
  readonly id: string;
  readonly name: string;
  readonly icao: string | null;
  readonly iata: string | null;
  readonly type: number;
  readonly at: LatLng;
  readonly elevationM: number | null;
  readonly ppr: boolean;
  readonly private: boolean;
  readonly frequencies: readonly OpenAipFrequency[];
  readonly runways: readonly OpenAipRunway[];
}

export interface OpenAipNavaid {
  readonly id: string;
  readonly name: string;
  readonly identifier: string;
  readonly type: number;
  readonly at: LatLng;
  readonly elevationM: number | null;
  /** "114.100 MHz", "389.000 kHz". */
  readonly frequency: string | null;
  readonly channel: string | null;
}

export interface OpenAipReportingPoint {
  readonly id: string;
  readonly name: string;
  readonly compulsory: boolean;
  readonly at: LatLng;
  readonly elevationM: number | null;
}

/** One area list read: the items Teslyn could read and how many it had to leave out. */
export interface OpenAipPage<T> {
  readonly items: T[];
  /** Items without a readable position or shape, left out (counted, never silent). */
  readonly skipped: number;
  /** The page was full: the next page must be asked. */
  readonly full: boolean;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);

function limit(v: unknown): OpenAipLimit | null {
  if (!isObj(v)) return null;
  const value = num(v.value);
  const unit = num(v.unit);
  const referenceDatum = num(v.referenceDatum);
  return value === null || unit === null || referenceDatum === null ? null : { value, unit, referenceDatum };
}

function point(v: unknown): LatLng | null {
  if (!isObj(v) || v.type !== 'Point' || !Array.isArray(v.coordinates)) return null;
  const [lng, lat] = v.coordinates as unknown[];
  return typeof lat === 'number' && typeof lng === 'number' && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
    ? { lat, lng }
    : null;
}

function polygon(v: unknown): LatLng[][] | null {
  if (!isObj(v) || v.type !== 'Polygon' || !Array.isArray(v.coordinates)) return null;
  const rings: LatLng[][] = [];
  for (const r of v.coordinates as unknown[]) {
    if (!Array.isArray(r)) return null;
    const ring: LatLng[] = [];
    for (const c of r as unknown[]) {
      if (!Array.isArray(c) || typeof c[0] !== 'number' || typeof c[1] !== 'number') return null;
      ring.push({ lat: c[1], lng: c[0] });
    }
    if (ring.length >= 3) rings.push(ring);
  }
  return rings.length > 0 ? rings : null;
}

function metres(v: unknown): number | null {
  // airport, navaid and reporting point elevations are metres above MSL (unit 0, datum 1)
  return isObj(v) && v.unit === 0 ? num(v.value) : null;
}

function listItems(body: string): { items: unknown[]; limit: number } {
  let j: unknown;
  try {
    j = JSON.parse(body);
  } catch {
    throw new Error('OpenAIP sent an answer Teslyn cannot read.');
  }
  if (!isObj(j) || !Array.isArray(j.items)) throw new Error('OpenAIP sent an answer Teslyn cannot read.');
  return { items: j.items, limit: num(j.limit) ?? OPENAIP_PAGE_LIMIT };
}

function page<T>(body: string, read: (raw: Record<string, unknown>) => T | null): OpenAipPage<T> {
  const { items, limit: lim } = listItems(body);
  const out: T[] = [];
  let skipped = 0;
  for (const raw of items) {
    const t = isObj(raw) ? read(raw) : null;
    if (t) out.push(t);
    else skipped += 1;
  }
  return { items: out, skipped, full: items.length >= lim };
}

export const readOpenAipAirspace = (a: Record<string, unknown>): OpenAipAirspace | null => {
  const id = text(a._id);
  const name = text(a.name);
  const rings = polygon(a.geometry);
  const type = num(a.type);
  if (!id || !name || !rings || type === null) return null;
  return {
    id,
    name,
    type,
    icaoClass: num(a.icaoClass),
    activity: num(a.activity),
    byNotam: a.byNotam === true,
    onDemand: a.onDemand === true,
    onRequest: a.onRequest === true,
    country: text(a.country),
    upper: limit(a.upperLimit),
    lower: limit(a.lowerLimit),
    rings,
  };
};

/** An airspace list page (`openaip.airspaces`). */
export const parseOpenAipAirspaces = (body: string): OpenAipPage<OpenAipAirspace> =>
  page(body, readOpenAipAirspace);

/** An airport list page (`openaip.airports`). */
export function parseOpenAipAirports(body: string): OpenAipPage<OpenAipAirport> {
  return page(body, (a) => {
    const id = text(a._id);
    const name = text(a.name);
    const at = point(a.geometry);
    const type = num(a.type);
    if (!id || !name || !at || type === null) return null;
    const frequencies = (Array.isArray(a.frequencies) ? a.frequencies : []).filter(isObj).flatMap((f) => {
      const value = text(f.value);
      return value
        ? [{ value, type: num(f.type), name: text(f.name), primary: f.primary === true } as OpenAipFrequency]
        : [];
    });
    const runways = (Array.isArray(a.runways) ? a.runways : []).filter(isObj).flatMap((r) => {
      const designator = text(r.designator);
      if (!designator) return [];
      const dim = isObj(r.dimension) ? r.dimension : {};
      return [
        {
          designator,
          lengthM: metres(dim.length),
          widthM: metres(dim.width),
          surface: isObj(r.surface) ? num(r.surface.mainComposite) : null,
          operations: num(r.operations),
        } as OpenAipRunway,
      ];
    });
    return {
      id,
      name,
      icao: text(a.icaoCode),
      iata: text(a.iataCode),
      type,
      at,
      elevationM: metres(a.elevation),
      ppr: a.ppr === true,
      private: a.private === true,
      frequencies,
      runways,
    };
  });
}

/** A navaid list page (`openaip.navaids`). */
export function parseOpenAipNavaids(body: string): OpenAipPage<OpenAipNavaid> {
  return page(body, (a) => {
    const id = text(a._id);
    const name = text(a.name);
    const at = point(a.geometry);
    const type = num(a.type);
    if (!id || !name || !at || type === null) return null;
    const f = isObj(a.frequency) ? a.frequency : null;
    const fv = f ? text(f.value) : null;
    return {
      id,
      name,
      identifier: text(a.identifier) ?? '',
      type,
      at,
      elevationM: metres(a.elevation),
      frequency: fv ? `${fv} ${f!.unit === 1 ? 'kHz' : 'MHz'}` : null,
      channel: text(a.channel),
    };
  });
}

/** A reporting point list page (`openaip.reporting_points`). */
export function parseOpenAipReportingPoints(body: string): OpenAipPage<OpenAipReportingPoint> {
  return page(body, (a) => {
    const id = text(a._id);
    const name = text(a.name);
    const at = point(a.geometry);
    if (!id || !name || !at) return null;
    return { id, name, compulsory: a.compulsory === true, at, elevationM: metres(a.elevation) };
  });
}

// ------------------------------------------------------------------------------------------------ words

const fmt = (n: number): string => Math.round(n).toLocaleString('en-AU');

/** A vertical limit in the words of an Australian chart: "SFC", "4,500 ft AMSL", "FL160", "1,000 ft AGL". */
export function limitText(l: OpenAipLimit | null): string {
  if (!l) return 'Not stated';
  if (l.unit === 6) return `FL${String(Math.round(l.value)).padStart(3, '0')}`;
  if (l.referenceDatum === 0 && l.value === 0) return 'SFC';
  const unit = l.unit === 0 ? 'm' : 'ft';
  const datum = l.referenceDatum === 0 ? 'AGL' : l.referenceDatum === 1 ? 'AMSL' : 'STD';
  return `${fmt(l.value)} ${unit} ${datum}`;
}

/** "293 m (961 ft) AMSL". */
export const elevationText = (m: number | null): string =>
  m === null ? 'Not stated' : `${fmt(m)} m (${fmt(m / 0.3048)} ft) AMSL`;

const word = (table: Readonly<Record<number, string>>, v: number | null, unknown = 'Not stated'): string =>
  v === null ? unknown : (table[v] ?? `OpenAIP code ${v}`);

// ------------------------------------------------------------------------------------------------ groups and styles

/** The airspace groups of the layer panel, each switched on or off (the Diurnal sub-toggles, grouped). */
export type AirspaceGroup = 'rdp' | 'controlled' | 'aerodrome' | 'military' | 'activity' | 'other';

export interface AirspaceGroupDef {
  readonly id: AirspaceGroup;
  readonly label: string;
  readonly types: readonly number[];
  readonly defaultOn: boolean;
}

export const AIRSPACE_GROUPS: readonly AirspaceGroupDef[] = [
  { id: 'rdp', label: 'Restricted, danger and prohibited areas', types: [1, 2, 3], defaultOn: true },
  { id: 'controlled', label: 'Controlled airspace (CTR, TMA, CTA)', types: [4, 7, 26, 36], defaultOn: true },
  {
    id: 'aerodrome',
    label: 'Aerodrome and radio zones (ATZ, RMZ, TMZ)',
    types: [5, 6, 13, 20, 23, 24],
    defaultOn: true,
  },
  {
    id: 'military',
    label: 'Military areas (TRA, TSA, MATZ, MTA)',
    types: [8, 9, 14, 16, 25, 30, 31],
    defaultOn: true,
  },
  {
    id: 'activity',
    label: 'Alert, warning and activity areas',
    types: [17, 18, 19, 21, 28, 29],
    defaultOn: true,
  },
  {
    id: 'other',
    label: 'FIR, sectors and other',
    types: [0, 10, 11, 12, 15, 22, 27, 32, 33, 34, 35],
    defaultOn: false,
  },
];

export const airspaceGroupOf = (type: number): AirspaceGroup =>
  AIRSPACE_GROUPS.find((g) => g.types.includes(type))?.id ?? 'other';

export const DEFAULT_HIDDEN_AIRSPACE: readonly AirspaceGroup[] = AIRSPACE_GROUPS.filter(
  (g) => !g.defaultOn,
).map((g) => g.id);

/** The Diurnal tool's airspace colours (`AIRSPACE_STYLES`), kept as tokens, the same in both themes. */
export type AviationColour =
  | 'airspace-restricted'
  | 'airspace-danger'
  | 'airspace-prohibited'
  | 'airspace-controlled'
  | 'airspace-military'
  | 'airspace-other'
  | 'aviation-airport'
  | 'aviation-navaid'
  | 'aviation-reporting';

export const AVIATION_TOKENS: readonly AviationColour[] = [
  'airspace-restricted',
  'airspace-danger',
  'airspace-prohibited',
  'airspace-controlled',
  'airspace-military',
  'airspace-other',
  'aviation-airport',
  'aviation-navaid',
  'aviation-reporting',
];

interface AirspaceLook {
  readonly colour: AviationColour;
  readonly word: string;
  readonly fill: number;
  readonly dashed: boolean;
}

function look(type: number): AirspaceLook {
  if (type === 1) return { colour: 'airspace-restricted', word: 'Restricted area', fill: 0.1, dashed: true };
  if (type === 2) return { colour: 'airspace-danger', word: 'Danger area', fill: 0.08, dashed: true };
  if (type === 3)
    return { colour: 'airspace-prohibited', word: 'Prohibited area', fill: 0.12, dashed: false };
  const g = airspaceGroupOf(type);
  if (g === 'controlled')
    return { colour: 'airspace-controlled', word: 'Controlled airspace', fill: 0.05, dashed: true };
  if (g === 'military')
    return { colour: 'airspace-military', word: 'Military area', fill: 0.05, dashed: true };
  return { colour: 'airspace-other', word: 'Other airspace', fill: 0.04, dashed: true };
}

const isRdp = (type: number): boolean => type === 1 || type === 2 || type === 3;

/** The legend of the airspace drawn: one row per look, plus "active by NOTAM" when one is. */
export const AIRSPACE_ACTIVE_FILL = 0.22;

/**
 * Every look the airspace layer can draw (restricted, danger and prohibited areas, each also active by NOTAM, then
 * controlled, military and other airspace), as `airspaceFeatures` words them: the map legend's "Show all"
 * (docs/specs/map.md §35.8).
 */
export function airspaceLegendAll(): LegendEntry[] {
  const typeOf = (g: AirspaceGroup): number => AIRSPACE_GROUPS.find((x) => x.id === g)?.types[0] ?? 0;
  const rows: LegendEntry[] = [];
  for (const t of [1, 2, 3]) {
    const l = look(t);
    rows.push({ colour: l.colour, stroke: 'thin', dashed: l.dashed, word: `Airspace: ${l.word}` });
    rows.push({
      colour: l.colour,
      stroke: 'thick',
      dashed: false,
      word: `Airspace: ${l.word}, active by NOTAM`,
    });
  }
  for (const t of [typeOf('controlled'), typeOf('military'), 0]) {
    const l = look(t);
    rows.push({ colour: l.colour, stroke: 'thin', dashed: l.dashed, word: `Airspace: ${l.word}` });
  }
  return rows;
}

// ------------------------------------------------------------------------------------------------ NOTAM activation

/**
 * The designator an OpenAIP airspace's name starts with ("R155A PEARCE [NOTAM]" → "R155A"), else the first one in
 * it (as the tool, `_extractAirspaceDesignator`); null for none.
 */
export function airspaceDesignator(name: string): string | null {
  const t = name.trim().toUpperCase();
  // military M areas, which OpenAIP types as danger areas, by the start of the name (decision 466: "M166 PEARCE")
  const m = /^([RDPM]\d+[A-Z]?)\b/.exec(t) ?? /\b([RDP]\d+[A-Z]?)\b/.exec(t);
  return m ? m[1]! : null;
}

/** "2610200000" (YYMMDDHHMM UTC) in ms, else null. */
function slotMs(s: string): number | null {
  const m = /^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(s);
  if (!m) return null;
  const t = Date.UTC(2000 + Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  return Number.isNaN(t) ? null : t;
}

export interface ActivationPeriod {
  readonly fromMs: number;
  readonly toMs: number;
}

/** The activation periods printed in a NOTAM ("2610190000 TO 2610190900"), in order (`_notamExtractTimeSlots`). */
export function notamActivationPeriods(n: Pick<NaipsNotam, 'raw'>): ActivationPeriod[] {
  const out: ActivationPeriod[] = [];
  for (const m of n.raw.matchAll(/\b(\d{10})\s+TO\s+(\d{10})\b/g)) {
    const fromMs = slotMs(m[1]!);
    const toMs = slotMs(m[2]!);
    if (fromMs !== null && toMs !== null && toMs > fromMs) out.push({ fromMs, toMs });
  }
  return out.sort((a, b) => a.fromMs - b.fromMs);
}

/** Words that make an area NOTAM say the area is not active. */
const DEACTIVATED = /\b(NOT ACT|NOT ACTIVE|DEACT|DEACTIVATED|CNL|CANCELLED|WITHDRAWN)\b/;

export interface AreaActivation {
  readonly designator: string;
  /** Active now, or activated later in the NOTAM's periods. */
  readonly state: 'active' | 'later';
  readonly notam: string;
  /** The period now in force (active) or the next one (later); null when the NOTAM prints none. */
  readonly period: ActivationPeriod | null;
  /** The NOTAM's lower and upper limits as printed ("SFC TO FL160"), else null. */
  readonly limits: string | null;
  /** The NOTAM's first line. */
  readonly line: string;
}

/**
 * Restricted, danger and prohibited areas the NOTAMs activate (04-010; the tool's `_parseActiveRpdFromNotams`), and
 * military M areas (decision 466): a NOTAM in force whose first line names an R, D, P or (with ACT) M designator,
 * not one that says the area is deactivated; when it prints activation periods, active only inside one (else
 * "later", with the next period); without periods, active while in force. An active entry wins over a later one for the same designator.
 */
export function areaActivations(notams: readonly NaipsNotam[], nowMs: number): Map<string, AreaActivation> {
  const out = new Map<string, AreaActivation>();
  for (const n of notams) {
    if (notamTimeState(n, nowMs) === 'expired') continue;
    const designators = notamDesignators(n);
    if (designators.length === 0) continue;
    const line = (n.text.split('\n')[0] ?? '').trim();
    if (DEACTIVATED.test(line.toUpperCase())) continue;
    const periods = notamActivationPeriods(n);
    const startsLater = notamTimeState(n, nowMs) === 'not_yet';
    let state: AreaActivation['state'];
    let period: ActivationPeriod | null = null;
    if (periods.length > 0) {
      const now = periods.find((p) => p.fromMs <= nowMs && nowMs < p.toMs);
      const next = periods.find((p) => p.fromMs > nowMs);
      if (now) {
        state = 'active';
        period = now;
      } else if (next) {
        state = 'later';
        period = next;
      } else continue;
    } else {
      state = startsLater ? 'later' : 'active';
    }
    const limitsLine = n.text.split('\n').find((l) => /\bTO\b/.test(l) && /(SFC|FT|FL\d|UNL|GND)/.test(l));
    const entry: AreaActivation = {
      designator: '',
      state,
      notam: n.id,
      period,
      limits: limitsLine ? limitsLine.trim() : null,
      line,
    };
    for (const d of designators) {
      const old = out.get(d);
      if (old && (old.state === 'active' || state !== 'active')) continue;
      out.set(d, { ...entry, designator: d });
    }
  }
  return out;
}

const periodText = (p: ActivationPeriod): string =>
  `${utcText(new Date(p.fromMs).toISOString())} to ${utcText(new Date(p.toMs).toISOString())}`;

/** What the loaded NOTAMs say about an R, D or P area, for its identify and tooltip. */
export function activationText(
  a: OpenAipAirspace,
  activation: AreaActivation | undefined,
  notamsLoaded: boolean,
): string {
  if (activation?.state === 'active') {
    const until = activation.period ? `, ${periodText(activation.period)}` : '';
    return `Active by NOTAM ${activation.notam}${until}${activation.limits ? ` (${activation.limits})` : ''}`;
  }
  if (activation?.state === 'later')
    return `Not active now; activated by NOTAM ${activation.notam}${activation.period ? ` ${periodText(activation.period)}` : ' later'}`;
  if (a.country && a.country !== 'AU') return 'Activation not known: NAIPS NOTAMs cover Australia only';
  if (!notamsLoaded) return 'Activation not known: the NOTAM layer has no briefing loaded';
  return 'Not active: no activation NOTAM in the loaded briefings';
}

// ------------------------------------------------------------------------------------------------ features

/** Airspace is drawn from zoom 6 and labelled from zoom 7, as the tool (am:10104, am:10293). */
export const AIRSPACE_MIN_ZOOM = 6;
export const AIRSPACE_LABEL_ZOOM = 7;
/** Airports, navaids and reporting points from zoom 7. */
export const AVIATION_POINT_MIN_ZOOM = 7;

export interface AirspaceOptions {
  readonly hidden: readonly AirspaceGroup[];
  readonly activations: ReadonlyMap<string, AreaActivation>;
  readonly notamsLoaded: boolean;
  /** Draw the designator labels of R, D and P areas (zoom 7 and closer). */
  readonly labels: boolean;
}

export interface AirspaceLayer extends LayerResult {
  readonly legend: LegendEntry[];
  /** Designators of R, D and P areas drawn, so the NOTAM layer can say where an activated area is drawn. */
  readonly drawnDesignators: ReadonlySet<string>;
  readonly activeCount: number;
}

const st = (colour: MapColour, stroke: MapStroke, dashed: boolean, fill: number): FeatureStyle => ({
  colour,
  stroke,
  dashed,
  fill,
});

/**
 * The airspace layer: every airspace of the groups shown, in the tool's colours; an R, D or P area that a NOTAM in
 * force activates is drawn solid, thick and filled (the tool's activation style), the others thin, as the tool's
 * halved stroke. Each airspace once, whichever cells it came from.
 */
export function airspaceFeatures(items: readonly OpenAipAirspace[], o: AirspaceOptions): AirspaceLayer {
  const hidden = new Set(o.hidden);
  const seen = new Set<string>();
  const features: MapFeature[] = [];
  const legend = new Map<string, LegendEntry>();
  const drawn = new Set<string>();
  let activeCount = 0;
  for (const a of items) {
    if (seen.has(a.id) || hidden.has(airspaceGroupOf(a.type))) continue;
    seen.add(a.id);
    const l = look(a.type);
    const rdp = isRdp(a.type);
    const designator = rdp ? airspaceDesignator(a.name) : null;
    const act = designator ? o.activations.get(designator) : undefined;
    const active = act?.state === 'active';
    if (designator) drawn.add(designator);
    if (active) activeCount += 1;
    const style = active
      ? st(l.colour, 'thick', false, AIRSPACE_ACTIVE_FILL)
      : st(l.colour, 'thin', l.dashed, l.fill);
    const kindWord = active ? `${l.word}, active by NOTAM` : l.word;
    legend.set(kindWord, {
      colour: l.colour,
      stroke: style.stroke,
      dashed: style.dashed,
      word: `Airspace: ${kindWord}`,
    });
    const status = rdp ? activationText(a, act, o.notamsLoaded) : null;
    const identify: IdentifyRow[] = [
      { label: 'Airspace', value: a.name },
      { label: 'Type', value: `${kindWord} (OpenAIP: ${typeWord(a.type)})` },
      { label: 'Class', value: word(OPENAIP_ICAO_CLASSES, a.icaoClass) },
      { label: 'Lower limit', value: limitText(a.lower) },
      { label: 'Upper limit', value: limitText(a.upper) },
      ...(status ? [{ label: 'NOTAM', value: status }] : []),
      ...(a.byNotam || a.onDemand || a.onRequest
        ? [
            {
              label: 'Activated',
              value: [
                a.byNotam ? 'by NOTAM' : '',
                a.onDemand ? 'on demand' : '',
                a.onRequest ? 'on request' : '',
              ]
                .filter(Boolean)
                .join(', '),
            },
          ]
        : []),
      ...(a.activity ? [{ label: 'Activity', value: word(OPENAIP_ACTIVITIES, a.activity) }] : []),
      { label: 'Source', value: 'OpenAIP (openaip.net), CC BY-NC 4.0' },
    ];
    const outer = a.rings[0]!;
    const centre = ringCentre(outer);
    features.push({
      id: a.id,
      layer: 'airspace',
      geometry: { type: 'polygon', rings: a.rings },
      style,
      tooltip: `${a.name} · ${kindWord}${status && rdp && act ? ` · ${act.state === 'active' ? 'active' : 'activated later'} (NOTAM ${act.notam})` : ''}`,
      ...(o.labels && designator
        ? { label: active ? `${designator} active` : designator, label_at: centre }
        : {}),
      identify,
    });
  }
  return { features, notDrawn: [], legend: [...legend.values()], drawnDesignators: drawn, activeCount };
}

const typeWord = (type: number): string => OPENAIP_AIRSPACE_TYPES[type] ?? `OpenAIP code ${type}`;

function ringCentre(r: readonly LatLng[]): LatLng {
  let lat = 0;
  let lng = 0;
  for (const p of r) {
    lat += p.lat;
    lng += p.lng;
  }
  return { lat: lat / r.length, lng: lng / r.length };
}

const runwayText = (r: OpenAipRunway): string =>
  [
    r.designator,
    r.lengthM !== null ? `${fmt(r.lengthM)} m` : '',
    r.surface !== null && r.surface !== 22
      ? (OPENAIP_SURFACES[r.surface] ?? '').toLowerCase()
      : 'surface not stated',
    r.operations === 1 ? 'temporarily closed' : r.operations === 2 ? 'closed' : '',
  ]
    .filter(Boolean)
    .join(', ');

/** Runway ends as pairs: "10/28, 1,499 m, surface not stated". */
function runwaysText(rs: readonly OpenAipRunway[]): string {
  const done = new Set<string>();
  const out: string[] = [];
  for (const r of rs) {
    if (done.has(r.designator)) continue;
    const opposite = rs.find(
      (o) => o !== r && !done.has(o.designator) && o.lengthM === r.lengthM && o.surface === r.surface,
    );
    done.add(r.designator);
    if (opposite) {
      done.add(opposite.designator);
      out.push(runwayText({ ...r, designator: `${r.designator}/${opposite.designator}` }));
    } else out.push(runwayText(r));
  }
  return out.join('; ');
}

const titleCase = (s: string): string =>
  s
    .toLowerCase()
    .replace(/\b([a-z])/g, (c) => c.toUpperCase())
    .replace(/\b(Wa|Nt|Qld|Nsw|Sa|Vic|Tas|Act)\b/g, (c) => c.toUpperCase());

const pointStyle = (colour: AviationColour): FeatureStyle => st(colour, 'thin', false, 0.6);

export const AVIATION_POINT_LEGEND: readonly LegendEntry[] = [
  { colour: 'aviation-airport', stroke: 'normal', dashed: false, word: 'Airport or airfield (OpenAIP)' },
  { colour: 'aviation-navaid', stroke: 'normal', dashed: false, word: 'Navaid (OpenAIP)' },
  { colour: 'aviation-reporting', stroke: 'normal', dashed: false, word: 'Reporting point (OpenAIP)' },
];

/** An airport chosen on the map for a plan (N18, decision 638): its name with its ICAO code, and its position. */
export interface PlanAirportChoice {
  /** "Meekatharra (YMEK)", or the name alone when it has no ICAO code. */
  readonly name: string;
  readonly icao: string | null;
  readonly at: LatLng;
}

/**
 * The plan's airport (or alternate) from an airport's card on the map (N18, decision 638: "Use as the plan's airport"
 * and "Use as the alternate"): the airport's own name and ICAO code from its identify rows and its point. Null for
 * anything that is not an airport point of the airports layer.
 */
export function planAirportChoice(
  f: Pick<MapFeature, 'layer' | 'geometry' | 'identify'>,
): PlanAirportChoice | null {
  if (f.layer !== 'airports' || f.geometry.type !== 'point') return null;
  const row = (label: string): string => f.identify.find((r) => r.label === label)?.value.trim() ?? '';
  const name = row('Airport');
  const code = row('ICAO').toUpperCase();
  const icao = /^[A-Z]{4}$/.test(code) ? code : null;
  const shown = name && icao ? `${name} (${icao})` : name || icao || 'Airport';
  return { name: shown, icao, at: f.geometry.at };
}

/** Airports and airfields as points, named with their code and type; identify with runways and frequencies. */
export function airportFeatures(items: readonly OpenAipAirport[]): LayerResult {
  const seen = new Set<string>();
  const features: MapFeature[] = [];
  for (const a of items) {
    if (seen.has(a.id)) continue;
    seen.add(a.id);
    const type = word(OPENAIP_AIRPORT_TYPES, a.type);
    const name = titleCase(a.name);
    const freq = a.frequencies
      .map((f) => `${f.name ?? word(OPENAIP_FREQUENCY_TYPES, f.type, 'Frequency')} ${f.value} MHz`)
      .join('; ');
    features.push({
      id: a.id,
      layer: 'airports',
      geometry: { type: 'point', at: a.at },
      style: pointStyle('aviation-airport'),
      tooltip: `${a.icao ? `${a.icao} ` : ''}${name} · ${type}`,
      identify: [
        { label: 'Airport', value: name },
        ...(a.icao ? [{ label: 'ICAO', value: a.icao }] : []),
        ...(a.iata ? [{ label: 'IATA', value: a.iata }] : []),
        { label: 'Type', value: type },
        { label: 'Elevation', value: elevationText(a.elevationM) },
        { label: 'Runways', value: a.runways.length ? runwaysText(a.runways) : 'Not stated' },
        ...(freq ? [{ label: 'Frequencies', value: freq }] : []),
        ...(a.ppr || a.private
          ? [
              {
                label: 'Use',
                value: [a.private ? 'private' : '', a.ppr ? 'PPR' : ''].filter(Boolean).join(', '),
              },
            ]
          : []),
        { label: 'Source', value: 'OpenAIP (openaip.net), CC BY-NC 4.0' },
      ],
    });
  }
  return { features, notDrawn: [] };
}

/** Navaids as points: "MEK VOR-DME 114.100 MHz". */
export function navaidFeatures(items: readonly OpenAipNavaid[]): LayerResult {
  const seen = new Set<string>();
  const features: MapFeature[] = [];
  for (const n of items) {
    if (seen.has(n.id)) continue;
    seen.add(n.id);
    const type = word(OPENAIP_NAVAID_TYPES, n.type);
    features.push({
      id: n.id,
      layer: 'navaids',
      geometry: { type: 'point', at: n.at },
      style: pointStyle('aviation-navaid'),
      tooltip: `${n.identifier} ${type}${n.frequency ? ` ${n.frequency}` : ''} · ${titleCase(n.name)}`,
      identify: [
        { label: 'Navaid', value: titleCase(n.name) },
        { label: 'Identifier', value: n.identifier || 'Not stated' },
        { label: 'Type', value: type },
        ...(n.frequency ? [{ label: 'Frequency', value: n.frequency }] : []),
        ...(n.channel ? [{ label: 'Channel', value: n.channel }] : []),
        { label: 'Elevation', value: elevationText(n.elevationM) },
        { label: 'Source', value: 'OpenAIP (openaip.net), CC BY-NC 4.0' },
      ],
    });
  }
  return { features, notDrawn: [] };
}

/** Reporting points: compulsory or on request. */
export function reportingPointFeatures(items: readonly OpenAipReportingPoint[]): LayerResult {
  const seen = new Set<string>();
  const features: MapFeature[] = [];
  for (const r of items) {
    if (seen.has(r.id)) continue;
    seen.add(r.id);
    const kind = r.compulsory ? 'Compulsory reporting point' : 'Reporting point on request';
    features.push({
      id: r.id,
      layer: 'reporting',
      geometry: { type: 'point', at: r.at },
      style: pointStyle('aviation-reporting'),
      tooltip: `${r.name} · ${kind}`,
      identify: [
        { label: 'Reporting point', value: r.name },
        { label: 'Type', value: kind },
        { label: 'Elevation', value: elevationText(r.elevationM) },
        { label: 'Source', value: 'OpenAIP (openaip.net), CC BY-NC 4.0' },
      ],
    });
  }
  return { features, notDrawn: [] };
}

// ------------------------------------------------------------------------------------------------ cells and copies

/** Cell size of an area list request, degrees (inside OpenAIP's limit, `OPENAIP_MAX_BBOX_DEG`). */
export const OPENAIP_CELL_DEG = 2;
/** The project area is grown by this much before its cells are chosen. */
export const OPENAIP_PROJECT_MARGIN_KM = 50;
/** At most this many cells for one area (the project's, or a view asked for). */
export const OPENAIP_MAX_CELLS = 12;
/** A laptop copy older than this is asked again when online (airspace changes with the 28-day AIRAC cycle). */
export const OPENAIP_KEEP_MS = 7 * 86_400_000;
/**
 * Between two area list requests: OpenAIP rate-limits them hard (3 to 4 s apart drew "too many requests", 15 s apart
 * six in a row answered, staging proxy 20 Oct 2026); a project's first load takes a few minutes, then a week of
 * copies.
 */
export const OPENAIP_REQUEST_GAP_MS = 15_000;
/** After OpenAIP's "too many requests". */
export const OPENAIP_RATE_LIMIT_WAIT_MS = 60_000;

/** A cell as the proxy's bbox, "116,-26,118,-24". */
export type OpenAipCell = string;

/**
 * The fixed 2-degree cells covering `bounds` grown by `marginKm` (whole even degrees, so every window, laptop and
 * project around the same place asks the same cells and shares the tenant's hourly proxy answer); null when more
 * than `OPENAIP_MAX_CELLS` would be needed.
 */
export function openAipCells(bounds: GeoBounds, marginKm = 0): OpenAipCell[] | null {
  const dLat = marginKm / 111.32;
  const mid = ((bounds.south + bounds.north) / 2) * (Math.PI / 180);
  const dLng = marginKm / (111.32 * Math.max(0.1, Math.cos(mid)));
  const s = Math.max(-90, bounds.south - dLat);
  const n = Math.min(90, bounds.north + dLat);
  const w = Math.max(-180, bounds.west - dLng);
  const e = Math.min(180, bounds.east + dLng);
  const c = OPENAIP_CELL_DEG;
  const x0 = Math.floor(w / c) * c;
  const y0 = Math.floor(s / c) * c;
  const x1 = Math.ceil(e / c) * c;
  const y1 = Math.ceil(n / c) * c;
  const count = ((x1 - x0) / c) * ((y1 - y0) / c);
  if (count > OPENAIP_MAX_CELLS || count <= 0) return null;
  const out: OpenAipCell[] = [];
  for (let y = y0; y < y1; y += c) for (let x = x0; x < x1; x += c) out.push(`${x},${y},${x + c},${y + c}`);
  return out;
}

/** The kinds of OpenAIP list the aviation layers read (the proxy operations). */
export const OPENAIP_KINDS: readonly OpenAipAreaOp[] = [
  'airspaces',
  'airports',
  'navaids',
  'reporting_points',
];

/** One cell of one kind kept on the laptop: the pages' bodies as OpenAIP sent them, and when. */
export interface OpenAipCellCopy {
  readonly version: 1;
  readonly kind: OpenAipAreaOp;
  readonly cell: OpenAipCell;
  readonly fetchedAt: string;
  readonly pages: readonly string[];
}

/** The laptop copy's key: "openaip.airspaces.116_-26_118_-24". */
export const openAipCopyKey = (kind: OpenAipAreaOp, cell: OpenAipCell): string =>
  `openaip.${kind}.${cell.replace(/,/g, '_')}`;

/** A stored copy made safe, else null. */
export function readOpenAipCopy(raw: unknown): OpenAipCellCopy | null {
  if (!isObj(raw) || raw.version !== 1 || typeof raw.fetchedAt !== 'string' || typeof raw.cell !== 'string')
    return null;
  if (typeof raw.kind !== 'string' || !(OPENAIP_KINDS as readonly string[]).includes(raw.kind)) return null;
  if (!Array.isArray(raw.pages) || !raw.pages.every((p) => typeof p === 'string')) return null;
  if (Number.isNaN(Date.parse(raw.fetchedAt))) return null;
  return {
    version: 1,
    kind: raw.kind as OpenAipAreaOp,
    cell: raw.cell,
    fetchedAt: raw.fetchedAt,
    pages: raw.pages as string[],
  };
}

/** Whether a copy should be asked again (none, or older than a week). */
export const openAipCopyDue = (copy: OpenAipCellCopy | null, nowMs: number): boolean =>
  !copy || nowMs - Date.parse(copy.fetchedAt) >= OPENAIP_KEEP_MS;

/** Whether one more page must be asked after `pages` (the last was full, and the most pages are not reached). */
export const openAipMorePages = (lastFull: boolean, pages: number): boolean =>
  lastFull && pages < OPENAIP_MAX_PAGES;
